<template>
  <li
    class="cart-drawer__item fade-item x"
    :data-id="item.id"
    role="group"
    aria-label="line item"
    :data-mtr-product-name="productTitle"
    :data-mtr-product-variant-name="
      item?.options_with_values?.find?.(
        (o) => o.name === 'Color' || o.name === 'Colour'
      )?.value
    "
  >
    <div
      class="f cart-drawer__item-wrapper"
      :class="{ 'has-shipping-note': shippingNote }"
    >
      <div v-on:click="handleProductClick" class="cart-drawer__item-image">
        <img
          :src="cutOutImage ? cutOutImage : item.image"
          :class="[`cutout-${item.handle}`]"
          :alt="item.title"
        />
      </div>

      <div class="__content pl1 f y ais jcb x cart-drawer__item-details rel">
        <div class="x y">
          <div class="f jcb x y">
            <div class="f-a y f fdc" style="position: relative">
              <a
                :href="item.url"
                @click="handleProductClick"
                :aria-label="`link to ${item.title}`"
                class="cart-drawer__item-title"
              >
                <h2>{{ productPrefix }} {{ productTitle }}</h2>
              </a>
              <!-- Item Options -->
              <div class="f lg--fdr sm--fdc">
                <template v-for="(option, index) in item.options_with_values">
                  <div
                    v-bind:key="option.value"
                    v-if="option.value !== 'Default Title'"
                    class="cart-drawer__item-subtext"
                  >
                    {{ option.name }}:
                    <span
                      >{{ option.value }}
                      {{ subtitle ? `- ${subtitle}` : "" }}</span
                    >
                    <span
                      class="cart-drawer_item-subtext-separator"
                      v-if="index < item.options_with_values.length - 1"
                      >|&nbsp;</span
                    >
                  </div>
                </template>
              </div>
              <!-- Shipping Note -->
              <div v-if="shippingNote" class="cart-drawer--shipping-note">
                {{ shippingNote }}
              </div>
              <div class="f aic x cart-selectors bold lh-10 fs-10 plaid-l">
                <button
                  aria-label="decrement item"
                  class="old-p-1 old-mr-1 cart-quantity bg-dark-beige"
                  data-mtr-as="clickQuantitySelector"
                  data-mtr-track="click"
                  data-mtr-target="minus"
                  :data-mtr-label="item.quantity"
                  @click="removeFromCart(item)"
                >
                  <div>-</div>
                </button>
                <div
                  class="old-p-1 bg-dark-beige"
                  aria-live="polite"
                  aria-atomic="true"
                  role="text"
                >
                  <span class="sr-only-text">{{
                    `"${item.title}, quantity "`
                  }}</span>
                  <span class="line-item-quantity">{{ item.quantity }}</span>
                </div>
                <button
                  :class="{ disabled }"
                  :disabled="disabled"
                  aria-label="increment item"
                  data-mtr-as="clickQuantitySelector"
                  data-mtr-track="click"
                  data-mtr-target="plus"
                  :data-mtr-label="item.quantity"
                  class="old-p-1 old-ml-1 cart-quantity bg-dark-beige"
                  @click="updateItem(item)"
                >
                  <div>+</div>
                </button>
              </div>
            </div>
            <div class="f fdc jcb">
              <button
                aria-label="remove item"
                class="button--reset"
                @click="removeItem(item)"
              >
                <svg-trash></svg-trash>
              </button>

              <div
                class="cart-drawer__item-price ws-nw"
                :class="{
                  'cart-drawer__item-discounted-price':
                    hasCompareAtPrice || isDiscounted,
                }"
              >
                <div
                  v-if="hasCompareAtPrice"
                  class="cart-drawer__item-original-price"
                  style="text-decoration: line-through"
                  role="text"
                >
                  <span class="sr-only-text">Original price:</span>
                  <span>{{ displayCompareAtPrice }}</span>
                </div>
                <div
                  v-if="!hasCompareAtPrice && isDiscounted"
                  class="cart-drawer__item-original-price"
                  style="text-decoration: line-through"
                  role="text"
                >
                  <span class="sr-only-text">Original price:</span>
                  <span>{{ discountedPrice }}</span>
                </div>
                <div role="text">
                  <span class="sr-only-text">Current price:</span>
                  <span>{{ price }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </li>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";

export default {
  name: "cart-drawer-line-item",
  props: {
    item: Object,
    index: Number,
  },
  methods: {
    handleProductClick(e) {
      if (this.learnMoreVariant) {
        e.preventDefault();
        const toggleModalEvent = new CustomEvent("product-modal:open", {
          detail: {
            ...this.learnMoreVariant,
          },
        });
        window.dispatchEvent(toggleModalEvent);
        return false;
      } else {
        window.location.href = this.item.url;
      }
    },
    async updateItem(item) {
      const { key, properties, quantity } = item;
      if (this.disabled) return false;
      await this.updateCart({
        key,
        quantity: quantity + 1,
        properties,
      });
    },
    ...mapActions({
      updateCart: "cart/updateCart",
      removeFromCart: "cart/removeFromCart",
      removeItem: "cart/removeItem",
    }),
  },
  computed: {
    ...mapState({
      cart: (state) => state.cart.data,
    }),
    ...mapGetters({
      getFormattedPrice: "cart/getFormattedPrice",
      getItemsByIds: "cart/getItemsByIds",
      getProductQuantity: "cart/getProductQuantity",
      getVariantQuantity: "cart/getVariantQuantity",
      getVariantLearnMore: "metafields/getVariantLearnMore",
      getProductSubtitle: "metafields/getProductSubtitle",
      getProductTitle: "metafields/getProductTitle",
      getProductPrefix: "metafields/getProductPrefix",
      getVariantShippingNote: "metafields/getVariantShippingNote",
      getVariantCutOutImage: "metafields/getVariantCutOutImage",
      getVariantCompareAtPrice: "metafields/getVariantCompareAtPrice",
      getVariantQuantityLimit: "metafields/getVariantQuantityLimit",
    }),
    learnMoreVariant() {
      return this.getVariantLearnMore(this.item.id);
    },
    subtitle() {
      return this.getProductSubtitle(this.item.product_id);
    },
    productTitle() {
      return this.getProductTitle(this.item.product_id);
    },
    productPrefix() {
      return this.getProductPrefix(this.item.product_id);
    },
    shippingNote() {
      return this.getVariantShippingNote(this.item.id);
    },
    cutOutImage() {
      return this.getVariantCutOutImage(this.item.id);
    },
    compareAtPrice() {
      return this.getVariantCompareAtPrice(this.item.id);
    },
    quantityLimit() {
      return this.getVariantQuantityLimit(this.item.id);
    },
    productQuantity() {
      return this.getProductQuantity(this.item.product_id);
    },
    variantQuantity() {
      return this.getVariantQuantity(this.item.id);
    },
    hasCompareAtPrice() {
      return this.compareAtPrice > 0;
    },
    displayCompareAtPrice() {
      return this.getFormattedPrice(this.compareAtPrice * this.item.quantity);
    },
    isDiscounted() {
      return this.item.line_price < this.item.original_line_price;
    },
    discountedPrice() {
      return this.getFormattedPrice(this.item.original_line_price);
    },
    price() {
      if (this.item.line_price <= 0) return "FREE";
      return this.getFormattedPrice(this.item.line_price);
    },
    disabled() {
      if (
        this.quantityLimit !== null &&
        this.quantityLimit >= this.variantQuantity
      ) {
        return true;
      }

      const itemsToReact = this.getItemsByIds(this.item.properties._reactive);
      if (!itemsToReact || !itemsToReact.length) return false;
      const itemsToReactQuantity = itemsToReact.reduce(
        (totalQuantity, _item) => totalQuantity + _item.quantity,
        0
      );
      if (itemsToReactQuantity <= this.productQuantity) {
        return true;
      }
    },
  },
};
</script>
